import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  FileSpreadsheet,
  FolderTree,
  Loader2,
  Search,
  ShieldCheck,
} from "lucide-react";
import {
  checkUpgradeReadiness,
  downloadUpgradeReadinessCsv,
  type CompatibilityFinding,
  type Severity,
  type UpgradeReadinessReport,
  type UpgradeTarget,
} from "@/lib/api";
import { SeverityBadge } from "@/components/SeverityBadge";
import { ContentPicker } from "@/components/ContentPicker";
import { formatDate } from "@/lib/format";

const TARGETS: { value: UpgradeTarget; label: string; subtitle: string }[] = [
  {
    value: "COGNOS_12_FROM_11",
    label: "Cognos 11 → 12",
    subtitle: "IBM Cognos Analytics 11.x to 12.x",
  },
  {
    value: "POWERBI_PREMIUM",
    label: "Power BI Premium",
    subtitle: "Cognos to Microsoft Power BI Premium",
  },
  {
    value: "TABLEAU_CLOUD",
    label: "Tableau Cloud",
    subtitle: "Cognos to Salesforce Tableau Cloud",
  },
  {
    value: "QLIK_SENSE",
    label: "Qlik Sense",
    subtitle: "Cognos to Qlik Sense Enterprise / SaaS",
  },
];

// Wire cap. Matches the curate-api server default. Bigger numbers
// degrade rendering — the CSV download is the escape hatch.
const WIRE_CAP = 500;

type SeverityFilter = "all" | Severity;

export function UpgradeReadinessPage() {
  const [folderPath, setFolderPath] = useState("");
  const [target, setTarget] = useState<UpgradeTarget>("COGNOS_12_FROM_11");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [search, setSearch] = useState("");
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const scan = useMutation<UpgradeReadinessReport, Error>({
    mutationFn: () =>
      checkUpgradeReadiness(
        folderPath.trim() || null,
        target,
        severityToWire(severityFilter),
        WIRE_CAP,
      ),
  });

  async function onDownloadCsv() {
    setCsvDownloading(true);
    setCsvError(null);
    try {
      await downloadUpgradeReadinessCsv(
        folderPath.trim() || null,
        target,
        severityToWire(severityFilter),
      );
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : String(e));
    } finally {
      setCsvDownloading(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-curate-ink">
          Upgrade readiness
        </h1>
        <p className="mt-0.5 text-sm text-curate-slate">
          Pre-Upgrade Mode scan — flag assets that will block, warn, or
          change behaviour on the target BI platform. Pair with a cleanup
          plan to land a "cleanup-before-upgrade" dossier.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          scan.mutate();
        }}
        className="rounded-xl2 border border-curate-slate/20 bg-white p-4 shadow-card"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr,260px,auto]">
          <label className="block">
            <span className="mb-1 block text-2xs font-medium uppercase tracking-wide text-curate-slate">
              Folder scope
            </span>
            <div className="flex items-center gap-2 rounded-md border border-curate-slate/30 bg-white px-3">
              <FolderTree size={14} className="text-curate-slate" aria-hidden />
              <input
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                placeholder="leave empty to scan everything"
                className="w-full bg-transparent py-2 text-sm text-curate-ink placeholder:text-curate-slate focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="shrink-0 rounded border border-curate-slate/30 bg-white px-2 py-0.5 text-2xs font-medium text-curate-ink hover:border-curate-primaryDark"
                title="Browse Cognos folders"
              >
                Browse…
              </button>
            </div>
          </label>

          <label className="block">
            <span className="mb-1 block text-2xs font-medium uppercase tracking-wide text-curate-slate">
              Target platform
            </span>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value as UpgradeTarget)}
              className="w-full rounded-md border border-curate-slate/30 bg-white px-3 py-2 text-sm text-curate-ink focus:outline-none"
            >
              {TARGETS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={scan.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-curate-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-curate-primaryDark disabled:opacity-40"
            >
              {scan.isPending
                ? <Loader2 size={14} className="animate-spin" />
                : <ShieldCheck size={14} />}
              Run scan
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-curate-slate/10 pt-3">
          <span className="text-2xs uppercase tracking-wide text-curate-slate">
            Filter
          </span>
          <SeverityFilterBar value={severityFilter} onChange={setSeverityFilter} />
          <p className="ml-auto text-2xs text-curate-slate">
            {TARGETS.find((t) => t.value === target)?.subtitle}
          </p>
        </div>
      </form>

      {scan.error && (
        <div className="mt-4 rounded-xl2 border border-curate-danger/30 bg-curate-danger/5 p-4 text-sm text-curate-danger">
          Scan failed — {(scan.error as Error).message}
        </div>
      )}
      {csvError && (
        <div className="mt-4 rounded-xl2 border border-curate-danger/30 bg-curate-danger/5 p-4 text-sm text-curate-danger">
          CSV export failed — {csvError}
        </div>
      )}
      {scan.data && (
        <ReportBody
          report={scan.data}
          search={search}
          onSearchChange={setSearch}
          onDownloadCsv={onDownloadCsv}
          csvDownloading={csvDownloading}
        />
      )}

      <ContentPicker
        open={pickerOpen}
        title="Pick a Cognos folder to scan"
        initialPath={folderPath || undefined}
        onCancel={() => setPickerOpen(false)}
        onConfirm={(picked) => {
          setFolderPath(picked.path);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

function ReportBody({
  report,
  search,
  onSearchChange,
  onDownloadCsv,
  csvDownloading,
}: {
  report: UpgradeReadinessReport;
  search: string;
  onSearchChange: (s: string) => void;
  onDownloadCsv: () => void;
  csvDownloading: boolean;
}) {
  const counts = report.countsBySeverity;
  // Client-side search is cheap (a few hundred rows max — the wire cap
  // already protected us). Server-side severity is the heavy lift.
  const visible = useMemo(() => {
    if (!search.trim()) return report.findings;
    const needle = search.trim().toLowerCase();
    return report.findings.filter((f) =>
      f.title.toLowerCase().includes(needle) ||
      f.details.toLowerCase().includes(needle) ||
      f.assetId.value.toLowerCase().includes(needle) ||
      f.ruleId.toLowerCase().includes(needle),
    );
  }, [report.findings, search]);

  return (
    <section className="mt-6 space-y-4">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-curate-ink">
            Scan results
          </h2>
          <p className="mt-0.5 text-2xs text-curate-slate">
            {report.assetsScanned} asset{report.assetsScanned === 1 ? "" : "s"}
            {" "}scanned · {report.assetsWithIssues} with issues ·{" "}
            generated {formatDate(report.generatedAt)}
          </p>
        </div>
        <div data-tour-id="upgrade-summary" className="ml-auto flex flex-wrap items-center gap-2">
          <CountChip label="Blockers" count={counts.BLOCKER ?? 0} variant="danger" />
          <CountChip label="Warnings" count={counts.WARN ?? 0} variant="warn" />
          <CountChip label="Info" count={counts.INFO ?? 0} variant="subtle" />
          <button
            type="button"
            onClick={onDownloadCsv}
            disabled={csvDownloading || report.totalFindings === 0}
            className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-3 py-1.5 text-2xs font-medium text-curate-ink shadow-sm hover:bg-curate-mist disabled:opacity-40"
            title="Download every finding (no cap) as CSV"
          >
            {csvDownloading ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Download CSV
          </button>
        </div>
      </header>

      {report.truncated && (
        <div className="rounded-xl2 border border-curate-warn/30 bg-curate-warn/5 p-3 text-2xs text-curate-ink">
          Showing the first <span className="font-mono font-medium">{report.findings.length}</span>
          {" "}of <span className="font-mono font-medium">{report.totalFindings}</span> findings.
          The list is capped to keep the page responsive — narrow the
          severity filter or <span className="font-medium">Download CSV</span> for the full set.
        </div>
      )}

      {report.findings.length > 0 && (
        <div className="relative">
          <Search
            size={12}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-curate-slate"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search findings (title, asset, rule)…"
            aria-label="Search findings"
            className="w-full rounded-md border border-curate-slate/30 bg-white py-1.5 pl-8 pr-3 text-xs text-curate-ink placeholder:text-curate-slate focus:border-curate-primary focus:outline-none focus:ring-2 focus:ring-curate-primary/20"
          />
        </div>
      )}

      {report.findings.length === 0 ? (
        report.totalFindings === 0 ? (
          <div className="rounded-xl2 border border-dashed border-curate-success/30 bg-curate-success/5 p-6 text-center text-sm text-curate-success">
            <ShieldCheck className="mx-auto mb-2" size={24} aria-hidden />
            No findings — every asset in scope is clean for this target.
          </div>
        ) : (
          <div className="rounded-xl2 border border-dashed border-curate-slate/30 bg-white p-6 text-center text-sm text-curate-slate">
            No findings matched the current filter. Widen the severity filter
            or download the CSV for the full set.
          </div>
        )
      ) : (
        <>
          <div className="text-2xs text-curate-slate">
            Showing <span className="font-medium text-curate-ink">{visible.length}</span>
            {visible.length !== report.findings.length && (
              <> of <span className="font-medium text-curate-ink">{report.findings.length}</span></>
            )}
            {" "}finding{visible.length === 1 ? "" : "s"}
          </div>
          <ul data-tour-id="upgrade-blockers" className="space-y-2">
            {visible.map((f, i) => (
              <li key={i}>
                <FindingCard finding={f} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function SeverityFilterBar({
  value,
  onChange,
}: {
  value: SeverityFilter;
  onChange: (v: SeverityFilter) => void;
}) {
  const opts: { v: SeverityFilter; label: string }[] = [
    { v: "all", label: "Any severity" },
    { v: "BLOCKER", label: "Blockers" },
    { v: "WARN", label: "Warn+" },
    { v: "INFO", label: "Info+" },
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`rounded-md border px-2 py-0.5 text-2xs font-medium ${
            value === o.v
              ? "border-curate-primary bg-curate-primary text-white"
              : "border-curate-slate/30 bg-white text-curate-ink hover:bg-curate-mist"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function severityToWire(filter: SeverityFilter): Severity | null {
  return filter === "all" ? null : filter;
}

function FindingCard({ finding }: { finding: CompatibilityFinding }) {
  return (
    <article className="rounded-xl2 border border-curate-slate/20 bg-white p-3 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <SeverityBadge level={finding.severity} />
            <span className="font-mono text-2xs text-curate-slate">
              {finding.ruleId}
            </span>
          </div>
          <h3 className="mt-1 font-medium text-curate-ink">{finding.title}</h3>
          <p className="mt-0.5 text-2xs text-curate-slate">
            asset <code className="font-mono">{finding.assetId.value}</code>
          </p>
        </div>
        {finding.severity === "BLOCKER" && (
          <AlertTriangle size={14} className="text-curate-danger" aria-hidden />
        )}
      </div>
      {finding.details && (
        <p className="mt-2 text-sm text-curate-ink/85">{finding.details}</p>
      )}
      {finding.fixHint && (
        <div className="mt-2 rounded-md border border-curate-accent/30 bg-curate-accent/5 p-2 text-2xs text-curate-ink">
          <span className="font-semibold text-curate-primary">Fix hint:</span>{" "}
          {finding.fixHint}
        </div>
      )}
    </article>
  );
}

function CountChip({
  label,
  count,
  variant,
}: {
  label: string;
  count: number;
  variant: "danger" | "warn" | "subtle";
}) {
  const styles: Record<"danger" | "warn" | "subtle", string> = {
    danger: "bg-curate-danger/10 text-curate-danger border-curate-danger/30",
    warn: "bg-curate-warn/10 text-curate-warn border-curate-warn/30",
    subtle: "bg-curate-mist text-curate-ink border-curate-slate/25",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-2xs font-medium ${styles[variant]}`}
    >
      <span className="font-mono">{count}</span> {label}
    </span>
  );
}
